/**
 * Seeded "County Record Hub" mock data for the Directory Search tab.
 *
 * Shaped like a future backend response (one row per county) so the UI can be
 * repointed at a real query without changing component props.
 */

export type CountyAvailability = "arcgis" | "portal" | "foia";

export type CountyAssetClass = "Parcel GIS" | "Recorder/Deeds" | "DOT ROW" | "Tax Maps" | "Easement GIS";

export interface CountyHubLinks {
  /** County recorder / clerk document search. */
  recorderSearch?: string;
  /** Public web GIS viewer. */
  gisViewer?: string;
  /** Machine-readable REST service endpoint. */
  restEndpoint?: string;
  /** Human-readable link when the county disables the HTML services directory. */
  restBrowse?: string;
}

export interface CountyScanField {
  label: string;
  value: string;
}

export type PortalAccess = "free" | "paid";
export type ScrapeFeasibility = "high" | "medium" | "low";

export interface CountyPortal {
  name: string;
  access: PortalAccess;
  feasibility: ScrapeFeasibility;
  url: string;
}

export interface CountyRestLayer {
  id: number;
  name: string;
  geometryType: string;
  recordCount: number;
}

export interface CountyRecordHub {
  id: string;
  /** Search-term title, e.g. "Cook County, IL Recorder of Deeds & Clerk Recordings Search". */
  title: string;
  county: string;
  state: string;
  stateName: string;
  /** Five-digit county FIPS code. */
  fips: string;
  availability: CountyAvailability[];
  assetClasses: CountyAssetClass[];
  links: CountyHubLinks;
  summary: string;
  /** ISO date the hub links were last human-verified. */
  lastVerified: string;
  /** Percentage uptime/consistency of the county platforms, 0-100. */
  reliabilityScore: number;
  /** Direct access portals surfaced in the detail drawer. */
  portals: CountyPortal[];
  /** Published REST layers surfaced as a JSON preview. */
  restLayers: CountyRestLayer[];
  /** Search term / doc-index path used by the generated scraper snippet. */
  deedIndexUrl: string;
  /** Sample metadata returned by the mock automated scan. */
  scan: {
    durationMs: number;
    layersDiscovered: number;
    recordsSampled: number;
    lastPublished: string;
    format: string;
    fields: CountyScanField[];
    sampleRecord: Record<string, string | number>;
  };
}

export const AVAILABILITY_LABEL: Record<CountyAvailability, string> = {
  arcgis: "ArcGIS Endpoint",
  portal: "Web Portal",
  foia: "FOIA Required",
};

export const AVAILABILITY_BADGE: Record<CountyAvailability, string> = {
  arcgis: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300",
  portal: "border-sky-500/30 bg-sky-500/15 text-sky-300",
  foia: "border-amber-500/30 bg-amber-500/15 text-amber-300",
};

import { TOP_COUNTY_HUBS } from "./countyRecordHubsTop";
import { OPEN_GIS_COUNTY_HUBS } from "./countyRecordHubsOpenGis";
import { COUNTY_RECORD_HUB_COPY } from "./countyRecordHubCopy";
import { EXPANSION_COUNTY_HUBS, EXPANSION_HUB_COPY } from "./countyRecordHubsExpansion";

const CORE_COUNTY_HUBS: Omit<CountyRecordHub, "title">[] = [
  {
    id: "nd-dunn",
    county: "Dunn County",
    state: "ND",
    stateName: "North Dakota",
    fips: "38025",
    availability: ["arcgis", "portal"],
    assetClasses: ["Parcel GIS", "Recorder/Deeds", "DOT ROW"],
    links: {
      recorderSearch: "https://www.dunncountynd.gov/dunn-county-recorder",
      gisViewer: "https://www.arcgis.com/apps/webappviewer/index.html?id=dunn-county-nd",
      restEndpoint: "https://ndgishub.nd.gov/arcgis/rest/services/All_GovtBoundaries/MapServer",
    },
    summary:
      "Bakken-region county with statewide GIS hub coverage. Parcel and section-line ROW layers publish as open ArcGIS services; recorded instruments require the county portal.",
    lastVerified: "2026-08-13",
    reliabilityScore: 94,
    deedIndexUrl: "https://www.ndrin.com/counties/dunn/",
    portals: [
      { name: "Dunn County Recorder Index", access: "free", feasibility: "medium", url: "https://www.dunncountynd.gov/dunn-county-recorder" },
      { name: "ND GIS Hub REST Services", access: "free", feasibility: "high", url: "https://ndgishub.nd.gov/arcgis/rest/services" },
      { name: "NDDOT Roadway ROW Viewer", access: "free", feasibility: "medium", url: "https://www.dot.nd.gov/travel-and-safety/traveling-north-dakota/nddot-maps" },
    ],
    restLayers: [
      { id: 3, name: "Parcel Polygon", geometryType: "esriGeometryPolygon", recordCount: 7412 },
      { id: 7, name: "Section Line ROW", geometryType: "esriGeometryPolyline", recordCount: 3120 },
      { id: 9, name: "Utilities", geometryType: "esriGeometryPolyline", recordCount: 884 },
      { id: 10, name: "Railroads", geometryType: "esriGeometryPolyline", recordCount: 42 },
    ],
    scan: {
      durationMs: 1420,
      layersDiscovered: 11,
      recordsSampled: 250,
      lastPublished: "2026-06-18",
      format: "Esri JSON / GeoJSON",
      fields: [
        { label: "Service type", value: "MapServer (public, no token)" },
        { label: "Parcel layer", value: "Layer 3: Cadastral Parcels" },
        { label: "ROW layer", value: "Layer 7: Section Line ROW (33 ft each side)" },
        { label: "Projection", value: "EPSG:4326 (WGS 84)" },
      ],
      sampleRecord: {
        PARCEL_ID: "38025-0114-00230",
        OWNER: "REDACTED (public record)",
        DEED_BOOK: "412",
        DEED_PAGE: "88",
        ROW_WIDTH_FT: 66,
        ACRES: 158.4,
      },
    },
  },
  {
    id: "az-maricopa",
    county: "Maricopa County",
    state: "AZ",
    stateName: "Arizona",
    fips: "04013",
    availability: ["arcgis", "portal"],
    assetClasses: ["Parcel GIS", "Recorder/Deeds", "Tax Maps", "DOT ROW"],
    links: {
      recorderSearch: "https://recorder.maricopa.gov/recdocdata/",
      gisViewer: "https://maps.mcassessor.maricopa.gov/",
      restEndpoint: "https://gis.mcassessor.maricopa.gov/arcgis/rest/services",
    },
    summary:
      "One of the most complete open county stacks in the country. Assessor parcels, subdivision plats, and recorded easement documents are all queryable without a subscription.",
    lastVerified: "2026-08-14",
    reliabilityScore: 98,
    deedIndexUrl: "https://recorder.maricopa.gov/recdocdata/",
    portals: [
      { name: "Maricopa County Recorder", access: "free", feasibility: "high", url: "https://recorder.maricopa.gov/recdocdata/" },
      { name: "Assessor Parcel Viewer", access: "free", feasibility: "high", url: "https://maps.mcassessor.maricopa.gov/" },
      { name: "Certified Copy Ordering", access: "paid", feasibility: "low", url: "https://recorder.maricopa.gov/" },
    ],
    restLayers: [
      { id: 0, name: "Parcel Polygon", geometryType: "esriGeometryPolygon", recordCount: 1682440 },
      { id: 2, name: "Easement Lines", geometryType: "esriGeometryPolyline", recordCount: 214905 },
      { id: 4, name: "Utilities", geometryType: "esriGeometryPolyline", recordCount: 98120 },
      { id: 6, name: "Railroads", geometryType: "esriGeometryPolyline", recordCount: 311 },
    ],
    scan: {
      durationMs: 980,
      layersDiscovered: 34,
      recordsSampled: 500,
      lastPublished: "2026-08-01",
      format: "Esri JSON / GeoJSON / CSV",
      fields: [
        { label: "Service type", value: "FeatureServer (public, paged 2,000/req)" },
        { label: "Parcel layer", value: "Parcels/FeatureServer/0" },
        { label: "Easement layer", value: "PlatEasements/FeatureServer/2" },
        { label: "Doc search", value: "Recorder document index, 1871–present" },
      ],
      sampleRecord: {
        APN: "301-45-118A",
        SUBDIVISION: "DESERT RIDGE PARCEL 7",
        DOCKET: "2019-0448211",
        EASEMENT_TYPE: "Public Utility Easement",
        WIDTH_FT: 8,
      },
    },
  },
  {
    id: "tx-travis",
    county: "Travis County",
    state: "TX",
    stateName: "Texas",
    fips: "48453",
    availability: ["arcgis", "portal", "foia"],
    assetClasses: ["Parcel GIS", "Recorder/Deeds", "DOT ROW"],
    links: {
      recorderSearch: "https://countyclerk.traviscountytx.gov/departments/recording/search-copies-of-records/",
      gisViewer: "https://tnris.org/",
      restEndpoint: "https://services.arcgis.com/0L95CJ0VTaxqcmED/ArcGIS/rest/services",
    },
    summary:
      "Parcel and jurisdiction layers stream from the regional ArcGIS Online org. Full-text deed images are behind the clerk portal; TxDOT ROW maps for some corridors require a records request.",
    lastVerified: "2026-08-09",
    reliabilityScore: 88,
    deedIndexUrl: "https://countyclerk.traviscountytx.gov/departments/recording/search-copies-of-records/",
    portals: [
      { name: "Travis County Clerk Records", access: "free", feasibility: "medium", url: "https://countyclerk.traviscountytx.gov/departments/recording/search-copies-of-records/" },
      { name: "TNRIS / Texas GeoData", access: "free", feasibility: "high", url: "https://tnris.org/" },
      { name: "Deed Image Download", access: "paid", feasibility: "low", url: "https://countyclerk.traviscountytx.gov/" },
    ],
    restLayers: [
      { id: 0, name: "Parcel Polygon", geometryType: "esriGeometryPolygon", recordCount: 430118 },
      { id: 1, name: "Easement Lines", geometryType: "esriGeometryPolyline", recordCount: 51203 },
      { id: 3, name: "Utilities", geometryType: "esriGeometryPolyline", recordCount: 22740 },
      { id: 5, name: "Railroads", geometryType: "esriGeometryPolyline", recordCount: 96 },
    ],
    scan: {
      durationMs: 1760,
      layersDiscovered: 19,
      recordsSampled: 400,
      lastPublished: "2026-07-09",
      format: "GeoJSON / PDF (ROW maps)",
      fields: [
        { label: "Service type", value: "ArcGIS Online hosted FeatureServer" },
        { label: "Parcel layer", value: "Travis_Parcels/FeatureServer/0" },
        { label: "ROW source", value: "TxDOT ROW map PDFs, request-only for pre-1990" },
        { label: "Rate limit", value: "~1,000 features/request" },
      ],
      sampleRecord: {
        PROP_ID: "0234560102",
        LEGAL: "LOT 12 BLK C WESTGATE SEC 3",
        INSTRUMENT: "2021165544",
        EASEMENT_TYPE: "Drainage Easement",
        WIDTH_FT: 15,
      },
    },
  },
  {
    id: "nv-clark",
    county: "Clark County",
    state: "NV",
    stateName: "Nevada",
    fips: "32003",
    availability: ["arcgis", "portal"],
    assetClasses: ["Parcel GIS", "Recorder/Deeds", "Tax Maps"],
    links: {
      recorderSearch: "https://recorderecommerce.clarkcountynv.gov/recorder/web/",
      gisViewer: "https://maps.clarkcountynv.gov/gismo/",
      restEndpoint: "https://gisgate.co.clark.nv.us/arcgis/rest/services",
    },
    summary:
      "Open GISMO services expose parcels, assessor maps, and recorded plat easements. Utility corridor alignments for BLM-adjacent land are only partly digitized.",
    lastVerified: "2026-08-12",
    reliabilityScore: 96,
    deedIndexUrl: "https://recorderecommerce.clarkcountynv.gov/recorder/web/",
    portals: [
      { name: "Clark County Recorder eCommerce", access: "free", feasibility: "medium", url: "https://recorderecommerce.clarkcountynv.gov/recorder/web/" },
      { name: "GISMO Map Services", access: "free", feasibility: "high", url: "https://gisgate.co.clark.nv.us/arcgis/rest/services" },
      { name: "Assessor Map Images", access: "free", feasibility: "medium", url: "https://maps.clarkcountynv.gov/gismo/" },
    ],
    restLayers: [
      { id: 0, name: "Parcel Polygon", geometryType: "esriGeometryPolygon", recordCount: 812004 },
      { id: 3, name: "Easement Lines", geometryType: "esriGeometryPolyline", recordCount: 64311 },
      { id: 5, name: "Utilities", geometryType: "esriGeometryPolyline", recordCount: 40122 },
      { id: 8, name: "Railroads", geometryType: "esriGeometryPolyline", recordCount: 58 },
    ],
    scan: {
      durationMs: 1120,
      layersDiscovered: 27,
      recordsSampled: 500,
      lastPublished: "2026-07-28",
      format: "Esri JSON / TIFF (assessor maps)",
      fields: [
        { label: "Service type", value: "MapServer + ImageServer" },
        { label: "Parcel layer", value: "Parcels/MapServer/0" },
        { label: "Tax map source", value: "Scanned assessor plat books, georeferenced" },
        { label: "Auth", value: "None. Anonymous access" },
      ],
      sampleRecord: {
        PARCEL: "163-05-410-021",
        BOOK_PAGE: "163-05",
        INST_NO: "20240315-0001982",
        EASEMENT_TYPE: "Access & Utility Easement",
        WIDTH_FT: 20,
      },
    },
  },
  {
    id: "mi-kalamazoo",
    county: "Kalamazoo County",
    state: "MI",
    stateName: "Michigan",
    fips: "26077",
    availability: ["portal", "foia"],
    assetClasses: ["Parcel GIS", "Recorder/Deeds"],
    links: {
      recorderSearch: "https://www.kalcounty.gov/730/Register-of-Deeds",
      gisViewer: "https://www.kalcounty.gov/1207/GIS",
    },
    summary:
      "Legacy search portal only. No public REST service. Parcel exports and easement instrument lists are fulfilled through a FOIA request, typically within five business days.",
    lastVerified: "2026-07-30",
    reliabilityScore: 61,
    deedIndexUrl: "https://www.kalcounty.gov/730/Register-of-Deeds",
    portals: [
      { name: "Register of Deeds Search", access: "free", feasibility: "low", url: "https://www.kalcounty.gov/730/Register-of-Deeds" },
      { name: "County GIS Viewer", access: "free", feasibility: "low", url: "https://www.kalcounty.gov/1207/GIS" },
      { name: "Bulk Shapefile (FOIA)", access: "paid", feasibility: "low", url: "https://www.kalcounty.gov/1207/GIS" },
    ],
    restLayers: [],
    scan: {
      durationMs: 3240,
      layersDiscovered: 0,
      recordsSampled: 25,
      lastPublished: "2026-03-02",
      format: "HTML result pages / PDF",
      fields: [
        { label: "Service type", value: "Session-based ASP.NET search form" },
        { label: "Scan method", value: "Structured HTML parse, 1 req/sec courtesy limit" },
        { label: "Bulk data", value: "FOIA request required (shapefile, $25 media fee)" },
        { label: "Coverage gap", value: "Pre-1988 instruments not indexed digitally" },
      ],
      sampleRecord: {
        PARCEL_NO: "06-14-155-010",
        GRANTOR: "REDACTED (public record)",
        LIBER: "5821",
        PAGE: "441",
        DOC_TYPE: "Easement",
      },
    },
  },
  {
    id: "sc-beaufort",
    county: "Beaufort County",
    state: "SC",
    stateName: "South Carolina",
    fips: "45013",
    availability: ["arcgis", "portal"],
    assetClasses: ["Parcel GIS", "DOT ROW", "Tax Maps"],
    links: {
      recorderSearch: "https://rod.beaufortcountysc.gov/",
      gisViewer: "https://beaufortcountygis.maps.arcgis.com/",
      restEndpoint: "https://gis.beaufortcountysc.gov/arcgis/rest/services",
    },
    summary:
      "Coastal county with strong open data: parcels, road ROW centerlines, and OCRM critical-line layers all stream publicly. Deed images are indexed from 1785 forward.",
    lastVerified: "2026-08-15",
    reliabilityScore: 97,
    deedIndexUrl: "https://rod.beaufortcountysc.gov/",
    portals: [
      { name: "Register of Deeds Index", access: "free", feasibility: "high", url: "https://rod.beaufortcountysc.gov/" },
      { name: "Beaufort County GIS REST", access: "free", feasibility: "high", url: "https://gis.beaufortcountysc.gov/arcgis/rest/services" },
      { name: "ArcGIS Online Open Data", access: "free", feasibility: "high", url: "https://beaufortcountygis.maps.arcgis.com/" },
    ],
    restLayers: [
      { id: 1, name: "Parcel Polygon", geometryType: "esriGeometryPolygon", recordCount: 96233 },
      { id: 2, name: "Easement Lines", geometryType: "esriGeometryPolyline", recordCount: 10422 },
      { id: 4, name: "Road ROW / Utilities", geometryType: "esriGeometryPolyline", recordCount: 18904 },
      { id: 6, name: "Railroads", geometryType: "esriGeometryPolyline", recordCount: 27 },
    ],
    scan: {
      durationMs: 1340,
      layersDiscovered: 22,
      recordsSampled: 350,
      lastPublished: "2026-08-06",
      format: "GeoJSON / Esri JSON",
      fields: [
        { label: "Service type", value: "MapServer (public)" },
        { label: "Parcel layer", value: "Cadastral/MapServer/1" },
        { label: "ROW layer", value: "Transportation/MapServer/4: Road ROW" },
        { label: "Extras", value: "OCRM critical line, marsh buffer setbacks" },
      ],
      sampleRecord: {
        PIN: "R510 013 000 0247 0000",
        DISTRICT: "Hilton Head Island",
        DEED_BOOK: "3921",
        DEED_PAGE: "112",
        ROW_WIDTH_FT: 50,
      },
    },
  },
];

/** Titles and summaries come from one copy module so they stay search-oriented. */
export const COUNTY_RECORD_HUBS: CountyRecordHub[] = [...CORE_COUNTY_HUBS, ...TOP_COUNTY_HUBS, ...OPEN_GIS_COUNTY_HUBS, ...EXPANSION_COUNTY_HUBS].map(
  (hub) => {
    const copy = COUNTY_RECORD_HUB_COPY[hub.id] ?? EXPANSION_HUB_COPY[hub.id];
    return {
      ...hub,
      title: copy?.title ?? `${hub.county}, ${hub.state} Records Search`,
      summary: copy?.description ?? hub.summary,
    };
  },
);

export const COUNTY_HUB_STATES = Array.from(
  new Map(COUNTY_RECORD_HUBS.map((h) => [h.state, h.stateName])).entries(),
)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function filterCountyHubs(
  hubs: CountyRecordHub[],
  { state, counties }: { state: string; counties: string[] },
) {
  return hubs.filter((hub) => {
    if (state !== "all" && hub.state !== state) return false;
    if (counties.length > 0 && !counties.includes(hub.id)) return false;
    return true;
  });
}
